import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { getRandomTailwindColor } from "@/utils/tailwindColors";

import type { DocumentModel } from "@/models/DocumentModel";
import type { TailwindColor } from "@/utils/tailwindColors";
import type { Mark, Node as ProseMirrorNode } from "@tiptap/pm/model";

export const VARIABLE_LINK_MARK = "variableLink";

export type VariableLinkDecorations = {
  decorations: DecorationSet;
  linkedSymbols: Set<string>;
};

export function markSymbol(marks: readonly Mark[]): string | null {
  const mark = marks.find((m) => m.type.name === VARIABLE_LINK_MARK);
  const variable: unknown = mark?.attrs.variable;
  return typeof variable === "string" && variable ? variable : null;
}

// Links store the labeled symbol (x_1), so x_1 and x_2 resolve to different variables.
function findLinkedVariable(doc: DocumentModel, symbol: string): VariableNodeModel | undefined {
  return doc.getNodesByType(VariableNodeModel.type).find((node) => node.labeledValue === symbol);
}

export function getVariableLinkColor(doc: DocumentModel, symbol: string): TailwindColor {
  return findLinkedVariable(doc, symbol)?.color ?? getRandomTailwindColor(symbol, "slate");
}

export function computeVariableLinkDecorations(
  pmDoc: ProseMirrorNode,
  doc: DocumentModel,
): VariableLinkDecorations {
  const decorations: Decoration[] = [];
  const linkedSymbols = new Set<string>();
  const highlighted = doc.editor.highlightedVariableSymbols;
  const variableCache = new Map<string, VariableNodeModel | undefined>();
  const variableFor = (symbol: string) => {
    if (!variableCache.has(symbol)) {
      variableCache.set(symbol, findLinkedVariable(doc, symbol));
    }
    return variableCache.get(symbol);
  };

  pmDoc.descendants((node, pos) => {
    // Decorating over a math node view re-parents its DOM and blurs the in-math caret.
    if (nodeIsExpression(node)) {
      return false;
    }

    if (!node.isText) {
      return true;
    }

    const symbol = markSymbol(node.marks);
    if (!symbol) {
      return true;
    }
    linkedSymbols.add(symbol);

    const variable = variableFor(symbol);
    if (!variable) {
      decorations.push(
        Decoration.inline(pos, pos + node.nodeSize, {
          class: "underline decoration-dotted decoration-gray-400 underline-offset-2",
          "data-variable-link-dangling": "true",
        }),
      );
      return true;
    }

    const color = variable.color || getRandomTailwindColor(symbol, "slate");
    const isHighlighted = highlighted.has(symbol);
    decorations.push(
      Decoration.inline(pos, pos + node.nodeSize, {
        // decoration-transparent stays on the rest state: Tailwind emits it after decoration-<color>-600.
        class: [
          "underline decoration-2 underline-offset-4",
          "transition-[color,text-decoration-color] duration-200",
          isHighlighted
            ? `text-${color}-600 dark:text-${color}-400 decoration-${color}-600 dark:decoration-${color}-400`
            : `text-${color}-900 dark:text-${color}-100 decoration-transparent`,
        ].join(" "),
      }),
    );

    return true;
  });

  return { decorations: DecorationSet.create(pmDoc, decorations), linkedSymbols };
}
